import { useQuery } from '@tanstack/react-query';
import { formatQty } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { SectionCard } from '../ui/Detail';
import { StaleNotice, isNotFound, refreshFailed, shownState } from '../ui/QueryStates';

/** What the card says with no signal and nothing saved on this phone: NOT "no parts". */
export const PARTS_NOT_LOADED =
  'Parts list not loaded yet — it will show when you’re back online.';
export const NO_PARTS_PLANNED = 'No parts planned for this job.';

/**
 * The app's one retry (`APP_QUERY_DEFAULTS.retry: 1`, as `session-query.ts` spells it), except
 * for "not found": that is an answer, not a hiccup, and a retry would hold a saved list on
 * screen for the back-off after the server said the visit is no longer this tech's.
 */
export const retryUnlessNotFound = (failures: number, error: unknown): boolean =>
  !isNotFound(error) && failures < 1;

/**
 * "Parts for this job" on the tech's visit (ops insights Phase 6a, ticket 04): what the office
 * planned for the job, how many are used so far, how many are left, and how many the tech's
 * own van and the shop hold, so they load the van before they leave. Read-only: the list is the
 * office's to change (the job page), and using a part is the Parts used card below.
 *
 * ⚠️ The numbers are the server's (`jobParts.forVisit`), drawn as they come: the caller's own
 * van and the shop, never another tech's van (the projection has none to give), and no "short"
 * figure (spec §2 amendment: counted from one van it would disagree with the office's).
 *
 * The states, in the order they are checked (LESSONS 2026-07-30):
 *  - NOT_FOUND: the visit is no longer one this tech has still to do (finished, canceled, or
 *    they were taken off it). The list is the office's to read now, so the card goes, even
 *    over an answer saved earlier.
 *  - offline with nothing saved: says so. A paused read is `isPending` with no data, and
 *    drawing it as "No parts planned" would send a tech out without the compressor.
 *  - saved answer: drawn, offline or not (the persisted cache); a failed refresh says it is
 *    from the last time it loaded.
 */
export function JobPartsList({ visitId }: { visitId: string }) {
  const trpc = useTRPC();
  const q = useQuery({
    ...trpc.jobParts.forVisit.queryOptions({ visitId }),
    retry: retryUnlessNotFound,
  });
  if (isNotFound(q.error)) return null;

  const state = shownState(q, q.data);
  const lines = q.data?.lines ?? [];

  return (
    <SectionCard
      title={
        state === 'ready' && lines.length
          ? `Parts for this job (${lines.length})`
          : 'Parts for this job'
      }
    >
      <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
      {state === 'loading' ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : state === 'offline' ? (
        <p className="text-sm text-muted-foreground">{PARTS_NOT_LOADED}</p>
      ) : state === 'error' ? (
        <p className="text-sm text-[var(--sw-danger-text)]">
          Couldn’t load the parts for this job.{' '}
          <button type="button" className="underline" onClick={() => void q.refetch()}>
            Try again
          </button>
        </p>
      ) : lines.length === 0 ? (
        <p className="text-sm text-muted-foreground">{NO_PARTS_PLANNED}</p>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {lines.map((l) => (
            <li
              key={l.workOrderLineId ?? l.item.id}
              className="space-y-0.5 py-2 first:pt-0 last:pb-0"
            >
              <p className="break-words font-medium">
                {l.item.name}
                {l.item.sku ? (
                  <span className="font-normal text-muted-foreground">
                    {' '}
                    · {l.item.sku}
                  </span>
                ) : null}
              </p>
              {l.workOrderLineId ? (
                <p className="text-xs text-muted-foreground">
                  Task: {l.taskName || 'General work'} · Reserved{' '}
                  {formatQty(l.reservedHundredths ?? 0)}
                </p>
              ) : null}
              <p className="tabular-nums text-muted-foreground">
                Planned {formatQty(l.plannedHundredths)} · Used{' '}
                {formatQty(l.usedHundredths)} · Left{' '}
                <strong className="font-semibold text-foreground">
                  {formatQty(l.leftHundredths)}
                </strong>
              </p>
              <p className="tabular-nums">
                {l.inMyVanHundredths === null
                  ? 'No van set up'
                  : `In your van: ${formatQty(l.inMyVanHundredths)}`}
                <span className="text-muted-foreground"> · </span>
                Shop: {formatQty(l.shopHundredths)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
